/* =============================================================================
 * build-a-box.js — Lógica de la plantilla "Arma tu ramillete"
 * -----------------------------------------------------------------------------
 * JS vanilla, sin dependencias externas. Usa fetch contra el AJAX Cart API
 * nativo de Shopify (/cart/add.js).
 *
 * MODELO DE PRECIO — controlado por section.settings.pricing_mode (data-pricing-mode):
 *
 * - fixed_box (default): el precio de la caja es el precio de la variante
 *   seleccionada en Caja (50, 100, 150, 200, 300 piezas). Las variedades elegidas
 *   no alteran el precio. Al pagar se agrega UNA sola línea (la variante
 *   combinada Caja+Variedad si hay match, si no la variante de Caja) con
 *   QUANTITY = 1. Las flores seleccionadas se agregan como Line Item Properties.
 *
 * - sum_variants: el precio total es la suma de (precio de cada variedad x
 *   cantidad elegida). El precio de cada variedad no depende de la Caja. Al
 *   pagar se agrega UNA línea POR VARIEDAD elegida (la variante real combinada
 *   Caja+Variedad, para que el inventario correcto se descuente), con
 *   QUANTITY = cantidad elegida de esa variedad. Todas las líneas comparten una
 *   Line Item Property (bundlePropertyLabel) que identifica que pertenecen al
 *   mismo ramillete armado. Si falta la variante combinada para alguna
 *   variedad elegida, no se agrega nada al carrito (para no cobrar de menos ni
 *   de más) y se muestra un mensaje de error.
 * =============================================================================
 */

(function () {
  'use strict';

  /**
   * Inicializa una instancia de "Arma tu caja" sobre su contenedor raíz.
   */
  function BuildABox(root) {
    this.root = root;

    // ---- Config desde data-attributes ----
    this.cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
    this.allowRepeat = root.dataset.allowRepeat === 'true';
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.pricingMode = root.dataset.pricingMode === 'sum_variants' ? 'sum_variants' : 'fixed_box';
    this.bundlePropertyLabel = root.dataset.bundlePropertyLabel || 'Ramillete';
    
    try {
      this.variants = JSON.parse(root.dataset.productVariants || '[]');
    } catch (e) {
      this.variants = [];
    }

    this.optionCajaIndex = parseInt(root.dataset.optionCajaIndex || '1', 10);
    this.optionVariedadesIndex = parseInt(root.dataset.optionVariedadesIndex || '2', 10);

    try {
      this.strings = JSON.parse(root.dataset.strings || '{}');
    } catch (e) {
      this.strings = {};
    }

    // ---- Referencias al DOM ----
    this.variantBtns = Array.prototype.slice.call(root.querySelectorAll('[data-variant-btn]'));
    this.progressWrap = root.querySelector('[data-progress-wrap]');
    this.progressFill = root.querySelector('[data-progress-fill]');
    this.progressBar = root.querySelector('.build-a-box__progress-bar');
    this.countCurrentEl = root.querySelector('[data-count-current]');
    this.countTargetEl = root.querySelector('[data-count-target]');
    this.gridWrap = root.querySelector('[data-grid-wrap]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));
    this.footer = root.querySelector('[data-footer]');
    this.hint = root.querySelector('[data-hint]');
    this.payBtn = root.querySelector('[data-pay]');
    this.payLabel = root.querySelector('[data-pay-label]');
    this.spinner = root.querySelector('[data-spinner]');
    this.totalEl = root.querySelector('[data-total]');

    // ---- Estado ----
    this.baseVariantId = null;   // id de la variante representativa de la Caja
    this.baseVariantTitle = '';  // ej. "50", "100"
    this.baseVariantPrice = 0;   // precio de la caja (centavos)
    this.target = 0;             // objetivo de tallos según el tamaño
    this.total = 0;             // suma actual de tallos seleccionados
    this.totalPrice = 0;         // precio total a pagar (centavos)
    // selección: { [varietyName]: { qty, title, stepQty } }
    this.selection = {};
    this.isSubmitting = false;

    this.bindEvents();
  }

  /**
   * Extrae el valor del índice de opción dado para una variante.
   */
  BuildABox.prototype.getVariantOptionValue = function (variant, index) {
    if (!variant) return null;
    if (variant.options && variant.options.length >= index) {
      return variant.options[index - 1];
    }
    if (index === 1) return variant.option1;
    if (index === 2) return variant.option2;
    if (index === 3) return variant.option3;
    return null;
  };

  /**
   * Extrae el número de flores del título de la variante Caja.
   */
  BuildABox.prototype.parseTarget = function (title) {
    var match = String(title || '').match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  BuildABox.prototype.bindEvents = function () {
    var self = this;

    // --- Selección de variante Caja (paso 1) ---
    this.variantBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.selectVariant(btn);
      });
    });

    // --- Botones +/- de cada tarjeta ---
    this.cards.forEach(function (card) {
      var plus = card.querySelector('[data-plus]');
      var minus = card.querySelector('[data-minus]');
      if (plus) plus.addEventListener('click', function () { self.changeQty(card, +1); });
      if (minus) minus.addEventListener('click', function () { self.changeQty(card, -1); });
    });

    // --- Botón pagar ---
    if (this.payBtn) {
      this.payBtn.addEventListener('click', function () { self.checkout(); });
    }
  };

  /**
   * Selecciona una variante de tamaño. Resetea el contador y la selección del grid.
   */
  BuildABox.prototype.selectVariant = function (btn) {
    this.variantBtns.forEach(function (b) {
      b.classList.remove('is-selected');
      b.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');

    this.baseVariantId = btn.dataset.variantId;
    this.baseVariantTitle = btn.dataset.variantTitle || '';
    this.baseVariantPrice = parseInt(btn.dataset.variantPrice, 10) || 0;
    this.target = this.parseTarget(btn.dataset.variantTitle);

    this.resetSelection();

    // Mostrar progreso, grid y footer
    if (this.progressWrap) this.progressWrap.hidden = false;
    if (this.gridWrap) this.gridWrap.hidden = false;
    if (this.footer) this.footer.hidden = false;

    if (this.countTargetEl) this.countTargetEl.textContent = this.target;
    if (this.progressBar) this.progressBar.setAttribute('aria-valuemax', this.target);

    this.render();
  };

  /** Reinicia la selección y los contadores de todas las tarjetas. */
  BuildABox.prototype.resetSelection = function () {
    this.selection = {};
    this.total = 0;
    this.totalPrice = 0;
    this.cards.forEach(function (card) {
      var qtyEl = card.querySelector('[data-qty]');
      if (qtyEl) qtyEl.textContent = '0';
      card.classList.remove('is-active');
    });
  };

  /**
   * Suma o resta unidades de una variedad.
   * @param {HTMLElement} card
   * @param {number} delta +1 / -1
   */
  BuildABox.prototype.changeQty = function (card, delta) {
    if (!this.baseVariantId) return;                      // sin variante elegida
    if (card.dataset.available !== 'true') return;        // agotado

    var variantId = card.dataset.variantId;               // el valor de variedad
    var entry = this.selection[variantId];
    var currentQty = entry ? entry.qty : 0;
    
    var stepQty = parseInt(card.dataset.stepQty || '1', 10);

    // Validar límite global antes de sumar
    if (delta > 0 && (this.total + stepQty) > this.target) return;

    // Si no se permite repetir, el máximo es 1
    if (delta > 0 && !this.allowRepeat && currentQty >= 1) return;

    var newQty = currentQty + delta;
    if (newQty < 0) return;

    if (newQty === 0) {
      delete this.selection[variantId];
    } else {
      this.selection[variantId] = {
        qty: newQty,
        title: card.dataset.productTitle || '',
        stepQty: stepQty,
        price: parseInt(card.dataset.price || '0', 10)
      };
    }

    this.recalcTotal();

    // Actualizar contador visible
    var qtyEl = card.querySelector('[data-qty]');
    if (qtyEl) qtyEl.textContent = String(newQty);
    card.classList.toggle('is-active', newQty > 0);

    this.render();
  };

  BuildABox.prototype.recalcTotal = function () {
    var sum = 0;
    var priceSum = 0;
    for (var id in this.selection) {
      if (Object.prototype.hasOwnProperty.call(this.selection, id)) {
        var item = this.selection[id];
        sum += item.qty * item.stepQty;
        priceSum += item.qty * item.price;
      }
    }
    this.total = sum;
    if (this.pricingMode === 'sum_variants') {
      // El precio total es la suma del precio de cada variedad elegida
      this.totalPrice = priceSum;
    } else {
      // El precio total es el precio fijo de la variante de Caja elegida
      this.totalPrice = this.baseVariantPrice || 0;
    }
  };

  /**
   * Formatea centavos al formato de moneda de la tienda.
   */
  BuildABox.prototype.formatMoney = function (cents) {
    cents = cents || 0;
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, this.moneyFormat);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }
    var format = this.moneyFormat || '${{amount}}';
    var value = (cents / 100);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, token) {
      if (token === 'amount_no_decimals') return String(Math.round(value));
      return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    });
  };

  /**
   * Repinta progreso, estado de botones + y del botón PAGAR.
   */
  BuildABox.prototype.render = function () {
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(this.totalPrice);

    if (this.countCurrentEl) this.countCurrentEl.textContent = this.total;
    if (this.progressFill) {
      var pct = this.target > 0 ? Math.min(100, (this.total / this.target) * 100) : 0;
      this.progressFill.style.width = pct + '%';
    }
    if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', this.total);

    var self = this;
    this.cards.forEach(function (card) {
      var plus = card.querySelector('[data-plus]');
      if (!plus) return;
      if (card.dataset.available !== 'true') { plus.disabled = true; return; }

      var entry = self.selection[card.dataset.variantId];
      var qty = entry ? entry.qty : 0;
      var stepQty = parseInt(card.dataset.stepQty || '1', 10);

      var blockLimit = (self.total + stepQty) > self.target;
      var blockRepeat = !self.allowRepeat && qty >= 1;
      plus.disabled = blockLimit || blockRepeat;
    });

    var exact = this.target > 0 && this.total === this.target;
    if (this.payBtn) {
      this.payBtn.disabled = !exact || this.isSubmitting;
      this.payBtn.setAttribute('aria-disabled', String(!exact || this.isSubmitting));
      this.payBtn.classList.toggle('is-ready', exact);
    }
    if (this.hint) {
      if (exact) {
        this.hint.textContent = '';
      } else {
        var remaining = this.target - this.total;
        var pieces = this.strings.pieces || 'flores';
        this.hint.textContent = remaining > 0
          ? ('Remain ' + remaining + ' ' + pieces)
          : '';
      }
    }
  };

  /**
   * Busca la variante real que combina la Caja seleccionada (this.baseVariantTitle) con
   * una Variedad dada. Se usa tanto en modo fijo (una sola línea) como en modo suma
   * (una línea por variedad), porque el inventario/SKU siempre se descuenta de la
   * variante combinada real, aunque el precio de la variedad no dependa de la Caja.
   */
  BuildABox.prototype.findComboVariantId = function (varietyValue) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var valCaja = this.getVariantOptionValue(v, this.optionCajaIndex);
      var valVariety = this.getVariantOptionValue(v, this.optionVariedadesIndex);

      if (valCaja === this.baseVariantTitle && valVariety === varietyValue) {
        return v.id;
      }
    }
    return null;
  };

  /**
   * Intenta encontrar una variante del producto que coincida con la Caja seleccionada
   * y la primera variedad elegida, para reducir confusión en el carrito en modo fijo.
   */
  BuildABox.prototype.getCheckoutVariantId = function () {
    if (!this.baseVariantTitle) return this.baseVariantId;

    var selectedVarieties = [];
    for (var id in this.selection) {
      if (Object.prototype.hasOwnProperty.call(this.selection, id)) {
        selectedVarieties.push(id);
      }
    }

    if (selectedVarieties.length === 0) return this.baseVariantId;

    var comboId = this.findComboVariantId(selectedVarieties[0]);
    return comboId || this.baseVariantId;
  };

  /**
   * Agrega al carrito la(s) variante(s) correspondiente(s) según el modo de precio.
   */
  BuildABox.prototype.checkout = function () {
    if (this.isSubmitting) return;
    if (!this.baseVariantId || this.total !== this.target) return;

    var items;
    if (this.pricingMode === 'sum_variants') {
      items = this.buildSumVariantsItems();
      if (!items || items.length === 0) {
        var errMsg = this.strings.addError || 'Ocurrió un error al procesar tu ramillete. Inténtalo de nuevo.';
        if (this.hint) this.hint.textContent = errMsg;
        return;
      }
    } else {
      items = [
        {
          id: parseInt(this.getCheckoutVariantId(), 10),
          quantity: 1, // Se compra 1 caja
          properties: this.buildProperties()
        }
      ];
    }

    this.isSubmitting = true;
    this.setLoading(true);

    var payload = { items: items };

    var self = this;
    fetch(this.cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) { return res.json().then(function (j) { throw j; }); }
        return res.json();
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        self.isSubmitting = false;
        self.setLoading(false);
        self.render();
        var msg = (err && (err.description || err.errors)) || self.strings.addError ||
                  'Error al agregar al carrito.';
        if (self.hint) self.hint.textContent = msg;
        console.error('[build-a-box] add to cart failed:', err);
      });
  };

  /**
   * En modo suma, arma una línea de carrito por cada variedad elegida (variante real
   * Caja x Variedad, para que el inventario correcto se descuente), con la cantidad
   * de veces que se eligió esa variedad. Todas las líneas comparten una property que
   * identifica que pertenecen al mismo ramillete armado. Devuelve null si falta la
   * variante combinada para alguna variedad elegida — en ese caso no se debe cobrar
   * nada, porque el total mostrado ya no coincidiría con lo que se puede agregar.
   */
  BuildABox.prototype.buildSumVariantsItems = function () {
    var bundleId = 'RAM-' + Date.now().toString(36).toUpperCase() + '-' +
      Math.random().toString(36).slice(2, 6).toUpperCase();
    var bundleValue = this.baseVariantTitle + ' · ' + bundleId;
    var items = [];

    for (var id in this.selection) {
      if (!Object.prototype.hasOwnProperty.call(this.selection, id)) continue;
      var entry = this.selection[id];
      var comboId = this.findComboVariantId(id);
      if (!comboId) return null;

      var properties = {};
      properties[this.bundlePropertyLabel] = bundleValue;
      // Properties ocultas (prefijo _) para que /cart pueda agrupar visualmente
      // estas líneas como un solo ramillete, sin depender del texto editable
      // de arriba. Ver assets/build-a-box-cart-group.js.
      properties['_bundle_id'] = bundleId;
      properties['_bundle_size'] = this.baseVariantTitle;

      items.push({
        id: parseInt(comboId, 10),
        quantity: entry.qty,
        properties: properties
      });
    }

    return items;
  };

  /**
   * Genera las propiedades de línea del ramillete armado.
   */
  BuildABox.prototype.buildProperties = function () {
    var props = {};
    var seleccion = {};
    var prefix = this.strings.linePieceLabel || 'Flor';
    var i = 1;

    for (var id in this.selection) {
      if (!Object.prototype.hasOwnProperty.call(this.selection, id)) continue;
      var item = this.selection[id];
      props[prefix + ' ' + i] = item.title + ' x' + item.qty;
      seleccion[id] = item.qty;
      i++;
    }

    props['_seleccion'] = JSON.stringify(seleccion);
    return props;
  };

  /** Muestra/oculta el spinner. */
  BuildABox.prototype.setLoading = function (loading) {
    if (this.spinner) this.spinner.hidden = !loading;
    if (this.payBtn) this.payBtn.disabled = loading;
    if (this.payLabel) this.payLabel.style.opacity = loading ? '0.6' : '1';
  };

  // ---- Bootstrap ----
  function init() {
    var roots = document.querySelectorAll('[data-build-a-box]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__buildABoxInit) return;
      root.__buildABoxInit = true;
      new BuildABox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-build-a-box]');
    if (root && !root.__buildABoxInit) {
      root.__buildABoxInit = true;
      new BuildABox(root);
    }
  });
})();
